import {
  Component,
  Input,
  HostBinding,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconColor =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'gray'
  | '';

/**
 * Icon microcomponent atom for Material Design icons
 * Replaces mat-icon with Tailwind CSS implementation
 * Uses Material Icons font family
 *
 * Enhanced version that can be used as a drop-in replacement for mat-icon
 * including with Angular Material directives like matSuffix, matPrefix, etc.
 */
@Component({
  selector: 'app-icon, [appIcon]',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    <!-- Use mat-icon when used with Material directives like matSuffix, matPrefix, etc. -->
    <ng-container *ngIf="useMatIcon; else tailwindIcon">
      <mat-icon
        [fontSet]="fontSet"
        [inline]="inline"
        [color]="getMatIconColor()"
        [attr.aria-label]="ariaLabel"
        [attr.title]="title"
        >{{ name }}</mat-icon
      >
    </ng-container>

    <!-- Use Tailwind implementation by default -->
    <ng-template #tailwindIcon>
      <span
        class="material-icons select-none inline-flex items-center justify-center"
        [class]="getIconClasses()"
        [attr.aria-label]="ariaLabel"
        [attr.title]="title"
        [style.font-size.px]="getCustomSize()"
        role="img"
        >{{ name }}</span
      >
    </ng-template>
  `,
})
export class AppIconComponent implements AfterViewInit {
  /** Material icon name (e.g., 'home', 'search', 'menu') */
  @Input({ required: true }) name!: string;

  /** Icon size preset or custom number in pixels */
  @Input() size: IconSize | number = 'md';

  /** Icon color variant */
  @Input() color: IconColor = 'gray';

  /** Custom CSS classes */
  @Input() class: string = '';

  /** Accessibility label */
  @Input() ariaLabel?: string;

  /** Tooltip text */
  @Input() title?: string;

  /** Whether to use mat-icon implementation (auto-detected or can be forced) */
  @Input() useMatIcon: boolean = false;

  /** Font set for mat-icon (default is 'material-icons') */
  @Input() fontSet: string = 'material-icons';

  /** Whether the icon should be displayed inline (mat-icon property) */
  @Input() inline: boolean = false;

  // Attribute directive mode detection for use with Material directives
  @HostBinding('class.mat-icon-host')
  get isMatIconHost(): boolean {
    return this._isMatIconDirectiveHost;
  }

  // Flag to detect if we're being used as a host for Material directives
  private _isMatIconDirectiveHost = false;

  constructor(private _elementRef: ElementRef) {
    // Check if we have attributes that suggest we're being used with Material directives
    // This will be set via the @Input useMatIcon
    this._isMatIconDirectiveHost = this.useMatIcon;
  }

  // Lifecycle hook to detect Material directive presence
  ngAfterViewInit() {
    // This is a simple heuristic - more sophisticated detection would require specific token injection
    const hostElement = (this as any)?._elementRef?.nativeElement;
    if (
      hostElement &&
      (hostElement.hasAttribute('matSuffix') ||
        hostElement.hasAttribute('matPrefix') ||
        hostElement.hasAttribute('matDatepickerToggleIcon'))
    ) {
      this.useMatIcon = true;
      this._isMatIconDirectiveHost = true;
    }
  }

  /** Whether the icon should be clickable */
  @Input() clickable: boolean = false;

  /**
   * Get computed CSS classes for the icon
   */
  getIconClasses(): string {
    const classes = [
      this.getSizeClass(),
      this.getColorClass(),
      this.clickable
        ? 'cursor-pointer hover:opacity-80 transition-opacity'
        : '',
      this.class,
    ];

    return classes.filter(Boolean).join(' ');
  }

  /**
   * Converts our custom color to mat-icon compatible color
   */
  getMatIconColor(): string | undefined {
    const matColorMap: Record<IconColor, string | undefined> = {
      primary: 'primary',
      secondary: 'accent', // Material's accent is our secondary
      accent: 'accent',
      success: undefined, // Material doesn't have direct equivalents for these
      warning: undefined,
      error: 'warn', // Material's warn is our error
      info: 'primary', // Map info to primary for Material
      '': undefined, // Empty string maps to no color
      gray: undefined,
    };

    return matColorMap[this.color];
  }

  /**
   * Get size-based CSS classes
   */
  private getSizeClass(): string {
    if (typeof this.size === 'number') {
      return ''; // Custom size handled via style
    }

    const sizeMap: Record<IconSize, string> = {
      xs: 'text-xs', // 12px
      sm: 'text-sm', // 14px
      md: 'text-base', // 16px
      lg: 'text-lg', // 18px
      xl: 'text-xl', // 20px
    };

    return sizeMap[this.size] || sizeMap.md;
  }

  /**
   * Get color-based CSS classes
   */
  private getColorClass(): string {
    const colorMap: Record<IconColor, string> = {
      primary: 'text-primary-600',
      secondary: 'text-secondary-600',
      accent: 'text-accent-600',
      success: 'text-green-600',
      warning: 'text-yellow-600',
      error: 'text-red-600',
      info: 'text-blue-600',
      gray: 'text-gray-600',
      '': 'text-current', // Empty string uses the current text color
    };

    return colorMap[this.color] || colorMap.gray;
  }

  /**
   * Get custom font size in pixels for numeric sizes
   */
  getCustomSize(): number | null {
    return typeof this.size === 'number' ? this.size : null;
  }
}
